/**
 * Procedural sound effects for the billiards game, synthesized with the Web
 * Audio API — no external audio assets, no dependencies. Every sound is a tiny
 * (< 150 ms) one-shot built from oscillators and noise; nodes are created per
 * event and disposed automatically when they finish. A single master gain (low
 * by default so rapid hits don't clip) feeds the destination and doubles as the
 * mute switch.
 */

// `webkitAudioContext` is the legacy Safari prefix; declare both so TS allows the
// runtime feature-detection (the constructors may be absent under SSR).
type AudioContextCtor = typeof AudioContext;
interface AudioGlobal {
  AudioContext?: AudioContextCtor;
  webkitAudioContext?: AudioContextCtor;
}

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

export class SoundManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  /**
   * Master output level. Pushed up for a louder mix overall; a limiter on the bus
   * (see `resume`) catches the resulting peaks and overlapping break clicks so the
   * extra level doesn't clip.
   */
  private readonly masterLevel = 1.0;

  // Optional sample-based emote sounds. If a sample is loaded for an emote it plays
  // instead of the synthesized fallback, so dropping CC0 audio into public/sfx/
  // upgrades the emotes with no code change. See emote()/loadSamples().
  private sampleUrls: Record<string, string> | null = null;
  private samplesLoaded = false;
  private readonly samples = new Map<string, AudioBuffer>();

  constructor() {
    // No audio work here: a context created before a user gesture starts
    // suspended on most browsers, so we defer creation until `resume()`.
  }

  /** Lazily create/resume the AudioContext; call on the first user gesture to unlock audio. */
  resume(): void {
    let ctx = this.ctx;
    if (!ctx) {
      const g = globalThis as unknown as AudioGlobal;
      const Ctor = g.AudioContext ?? g.webkitAudioContext;
      if (!Ctor) return; // SSR or no Web Audio support — silently no-op.
      try {
        ctx = new Ctor();
        const master = ctx.createGain();
        master.gain.value = this.muted ? 0 : this.masterLevel;
        // Brick-wall-ish limiter so the louder master can't clip: it only grabs
        // peaks near full-scale (a hard hit, or many break clicks at once) and
        // leaves softer/medium events untouched, keeping their dynamics.
        const limiter = ctx.createDynamicsCompressor();
        limiter.threshold.value = -1.5; // start limiting just below 0 dBFS
        limiter.knee.value = 2;
        limiter.ratio.value = 12;
        limiter.attack.value = 0.001;
        limiter.release.value = 0.08;
        master.connect(limiter).connect(ctx.destination);
        this.ctx = ctx;
        this.master = master;
      } catch {
        this.ctx = null;
        this.master = null;
        return;
      }
    }
    // Browsers start the context suspended until a gesture resumes it.
    if (ctx.state === 'suspended') void ctx.resume();
    // first gesture: pull in any configured emote samples (best-effort, async)
    if (this.sampleUrls && !this.samplesLoaded) void this.loadSamples();
  }

  /**
   * Register sample URLs for emote sounds, keyed by emote-sound name (clap, cheer,
   * …). They're fetched lazily on the first audio gesture; any that 404 or fail to
   * decode simply fall back to the synthesized version. Drop files in public/sfx/.
   */
  setEmoteSamples(urls: Record<string, string>): void {
    this.sampleUrls = urls;
  }

  private async loadSamples(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx || !this.sampleUrls) return;
    this.samplesLoaded = true; // only attempt once
    for (const [name, url] of Object.entries(this.sampleUrls)) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue; // not provided yet → synth fallback
        const buf = await ctx.decodeAudioData(await res.arrayBuffer());
        this.samples.set(name, buf);
      } catch {
        /* missing/undecodable → keep the synthesized fallback */
      }
    }
  }

  /** Play a loaded sample by name; returns false if none is loaded. */
  private playSample(name: string): boolean {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return false;
    const buf = this.samples.get(name);
    if (!buf) return false;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = 0.9; // samples are already mixed; the bus limiter catches peaks
    src.connect(g).connect(master);
    src.start(ctx.currentTime);
    return true;
  }

  /**
   * Cue tip striking the ball — a soft, woody "thock". The leather tip cushions
   * the contact, so unlike the bright ball-ball click this is a low-mid knock with
   * a short, dry decay. The earlier version glided a 900→300 Hz tone over ~50 ms,
   * which rang out as a hollow, echoey "pew"; this replaces it with a couple of
   * fast-decaying low-mid partials over a brief, dull contact transient so it lands
   * as a tight thock with no tail. power01 in [0,1] scales loudness/brightness.
   */
  strike(power01: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const p = clamp01(power01);
    const t = ctx.currentTime;
    // Wide loudness range so a hard break clearly out-cracks a soft tap; full-power
    // strikes are driven hard into the master limiter for extra perceived loudness.
    const gain = 0.15 + 2.0 * Math.pow(p, 1.4);

    // 1) Contact transient — the brief, dull "tk" of the tip meeting the ball.
    // Band-limited (not a bright highpass hiss) and very short so it punctuates
    // the thock without adding a leathery sizzle or tail.
    const burstDur = 0.006;
    const noise = this.makeNoise(burstDur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1100 + 700 * p; // a touch brighter on harder hits
    bp.Q.value = 0.7;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(gain * 0.45, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + burstDur);
    noise.connect(bp).connect(ng).connect(master);
    noise.start(t);
    noise.stop(t + burstDur);

    // 2) The thock body — low-mid woody knock. A dominant ~200 Hz partial with a
    // fast decay gives the solid "thock"; a quieter upper partial adds the woody
    // edge. Small downward glide for a natural knock, but short enough that it
    // never rings hollow.
    this.decayTone(200 + 60 * p, gain * 1.0, 0.022, t, 'sine', 0.8);
    this.decayTone(330 + 90 * p, gain * 0.5, 0.013, t, 'triangle', 0.85);
    // 3) A faint, very short top for the initial snap — kept tiny so it doesn't
    // reintroduce the old bright/hollow character.
    this.decayTone(700 + 300 * p, gain * 0.22, 0.006, t, 'sine', 0.7);
  }

  /**
   * Ball-to-ball "clack": modal synthesis of two hard phenolic balls, tuned to a
   * spectral analysis of a real billiard collision recording. The measured signature
   * is a *bright, tight* click — ~70% of its energy lives in the 2–5 kHz band with
   * dominant modes near 1950 / 2300 / 2720 Hz, almost nothing below 800 Hz, and a
   * very fast (~8 ms) decay. (An earlier version centred the body around ~400 Hz,
   * which read as a low wooden "thock" — the opposite of a real phenolic click.)
   * So the voice is a cluster of high modal partials over a crisp HF contact tick,
   * with only a faint low thump for weight. intensity01 in [0,1] (impact speed)
   * scales pitch/brightness/loudness.
   */
  ballClick(intensity01: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const i = clamp01(intensity01);
    const t = ctx.currentTime;
    // Loudness rises *exponentially* with impact speed: each step up in intensity
    // multiplies the level, so the hardest collisions swell dramatically louder
    // than medium ones. The hardest hits are driven well past the bus ceiling on
    // purpose — the master limiter holds the peak while the extra drive packs more
    // energy under it, so a full-power smash reads markedly louder (not just at the
    // same clipped peak). e^(3.0) ≈ 20× span across the range.
    const gain = 0.1 * Math.exp(3.0 * i);

    // 1) Contact tick — the hard "ck" of the two surfaces meeting. Very short and
    // crisp; sells the *hardness*. Kept quiet so it punctuates rather than hisses.
    const clickDur = 0.0022;
    const tick = this.makeNoise(clickDur);
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 3000 + 2500 * i; // crisper with speed
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(gain * 0.5, t);
    tg.gain.exponentialRampToValueAtTime(0.0001, t + clickDur);
    tick.connect(hp).connect(tg).connect(master);
    tick.start(t);
    tick.stop(t + clickDur);

    // 2) Band-limited noise (1.6–3.4 kHz) for the non-tonal part of the click —
    // the "splat" of contact that keeps it from sounding like a pure beep. Centred
    // on the modal cluster with a wide-ish bandwidth and a very fast decay.
    const bodyDur = 0.01;
    const body = this.makeNoise(bodyDur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2300;
    bp.Q.value = 1.0; // ~1.6–3.4 kHz passband
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(gain * 0.6, t);
    bg.gain.exponentialRampToValueAtTime(0.0001, t + bodyDur);
    body.connect(bp).connect(bg).connect(master);
    body.start(t);
    body.stop(t + bodyDur);

    // 3) The phenolic "ring" — the dominant high modes measured in the real click.
    // Harder hits sit a touch higher in pitch (base), and each partial decays fast
    // (~6–9 ms) so it snaps rather than rings out.
    const base = 1 + 0.08 * i;
    this.decayTone(1950 * base, gain * 0.7, 0.009, t, 'sine', 0.9);
    this.decayTone(2300 * base, gain * 0.85, 0.008, t, 'sine', 0.9);
    this.decayTone(2720 * base, gain * 1.0, 0.0075, t, 'sine', 0.9);
    this.decayTone(3250 * base, gain * 0.45, 0.006, t, 'sine', 0.85);

    // 4) A faint, very short low thump for weight/density — kept low (~4% of energy)
    // so it adds body without dragging the click back down into a wooden thock.
    this.decayTone(95, gain * 0.06, 0.01, t, 'sine', 0.7);
  }

  /** One exponentially-decaying oscillator partial (modal voice), auto-disposed. */
  private decayTone(
    freq: number,
    peak: number,
    decay: number,
    t: number,
    type: OscillatorType,
    glide = 1,
  ): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (glide !== 1) osc.frequency.exponentialRampToValueAtTime(freq * glide, t + decay);
    const g = ctx.createGain();
    g.gain.setValueAtTime(peak, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
    osc.connect(g).connect(master);
    osc.start(t);
    osc.stop(t + decay);
  }

  /** Ball into a cushion (softer, lower thud). intensity01 in [0,1]. */
  cushion(intensity01: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const i = clamp01(intensity01);
    const t = ctx.currentTime;
    const dur = 0.06 + 0.06 * i; // slightly longer than a click
    const gain = 0.15 + 0.3 * i; // and quieter — a muffled thud

    // Low sine body — the dull "thud" of the rail.
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    const freq = 160 + 120 * i;
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.7, t + dur);
    const og = ctx.createGain();
    og.gain.setValueAtTime(gain, t);
    og.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(og).connect(master);
    osc.start(t);
    osc.stop(t + dur);

    // Low-pass-filtered noise for the soft cushion impact texture.
    const noise = this.makeNoise(dur);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 500 + 300 * i;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(gain * 0.7, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    noise.connect(lp).connect(ng).connect(master);
    noise.start(t);
    noise.stop(t + dur);
  }

  /**
   * Casino "chip" ding for the cushion counter (마지막 3쿠션 only).
   *
   * A bright two-partial bell that climbs a major scale with `n`, so a run of
   * cushions reads as an escalating win rather than four identical beeps. Kept
   * short and quiet — it layers ON TOP of the normal cushion thud, which is
   * still the sound of the actual contact.
   */
  cushionCount(n: number, target: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t = ctx.currentTime;
    // major-ish steps, so 1→2→3 rises; clamp so a long bank run stays musical
    const STEPS = [0, 4, 7, 12, 16, 19, 24];
    const semis = STEPS[Math.min(n - 1, STEPS.length - 1)] ?? 0;
    const base = 880 * Math.pow(2, semis / 12);
    const reached = n >= target;
    const dur = reached ? 0.5 : 0.22;

    // two partials an octave apart = a chime rather than a plain sine beep
    for (const [mult, level] of [[1, 0.22], [2, 0.1]] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(base * mult, t);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(level, t + 0.008); // fast, percussive attack
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(g).connect(master);
      osc.start(t);
      osc.stop(t + dur);
    }
  }

  /**
   * Jackpot flourish when the required cushion count is reached: a quick
   * arpeggio with a shimmer tail. This is the payoff sound, so it is the one
   * moment the audio is allowed to be loud and obviously game-showy.
   */
  jackpot(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t0 = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5]; // C E G C E
    notes.forEach((f, i) => {
      const t = t0 + i * 0.075;
      for (const [mult, level] of [[1, 0.2], [2, 0.08]] as const) {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(f * mult, t);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(level, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
        osc.connect(g).connect(master);
        osc.start(t);
        osc.stop(t + 0.42);
      }
    });
    // coin-shower shimmer: filtered noise swept up under the arpeggio
    const dur = 0.55;
    const noise = this.makeNoise(dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(1800, t0);
    bp.frequency.exponentialRampToValueAtTime(6000, t0 + dur);
    bp.Q.value = 1.2;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, t0);
    ng.gain.exponentialRampToValueAtTime(0.09, t0 + 0.08);
    ng.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    noise.connect(bp).connect(ng).connect(master);
    noise.start(t0);
    noise.stop(t0 + dur);
  }

  /**
   * Match win for the local player: a bigger, brighter version of `jackpot`'s
   * payoff — a rising major arpeggio plus a shimmer tail — since winning the
   * whole match deserves more than the per-shot bank/jackpot cues.
   */
  victory(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t0 = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5, 1568.0]; // C E G C E G
    notes.forEach((f, i) => {
      const t = t0 + i * 0.09;
      for (const [mult, level] of [[1, 0.24], [2, 0.1]] as const) {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(f * mult, t);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(level, t + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
        osc.connect(g).connect(master);
        osc.start(t);
        osc.stop(t + 0.55);
      }
    });
    // coin-shower shimmer, wider and longer than jackpot's
    const dur = 0.8;
    const noise = this.makeNoise(dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(1500, t0);
    bp.frequency.exponentialRampToValueAtTime(7000, t0 + dur);
    bp.Q.value = 1.0;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, t0);
    ng.gain.exponentialRampToValueAtTime(0.11, t0 + 0.1);
    ng.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    noise.connect(bp).connect(ng).connect(master);
    noise.start(t0);
    noise.stop(t0 + dur);
  }

  /**
   * Match loss for the local player: a low, descending minor-ish fall —
   * the deflated opposite of `victory`. Short and understated rather than
   * a jeer, since it's the player's own result landing, not a crowd reaction.
   */
  defeat(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t0 = ctx.currentTime;
    const notes = [440, 392.0, 349.23, 293.66]; // A G F D — a sagging descent
    notes.forEach((f, i) => {
      const t = t0 + i * 0.14;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, t);
      osc.frequency.exponentialRampToValueAtTime(f * 0.94, t + 0.3);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(g).connect(master);
      osc.start(t);
      osc.stop(t + 0.35);
    });
  }

  /**
   * Congratulatory chime for a carom that scored via a cushion/bank contact —
   * the everyday "nice bank shot!" payoff, not the 마지막 3쿠션 jackpot (that
   * stays reserved for the harder finish-rule win). A short three-note rise,
   * bright but quick, so it doesn't overstay its welcome on a run of banks.
   */
  bankScore(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t0 = ctx.currentTime;
    const notes = [659.25, 830.61, 987.77]; // E G B — a bright, unresolved-feeling rise
    notes.forEach((f, i) => {
      const t = t0 + i * 0.06;
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(f, t);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.16, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
      osc.connect(g).connect(master);
      osc.start(t);
      osc.stop(t + 0.22);
    });
  }

  /**
   * 당구 교실 step-cleared chime: a short, resolved two-note rise (unlike
   * `bankScore`'s deliberately unresolved one) — this one closes the thought,
   * since the tutorial moves on to the next lesson right behind it with no
   * button press to confirm the player noticed.
   */
  tutorialPass(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t0 = ctx.currentTime;
    const notes = [587.33, 880]; // D A — resolves upward, doesn't hang
    notes.forEach((f, i) => {
      const t = t0 + i * 0.09;
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(f, t);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      osc.connect(g).connect(master);
      osc.start(t);
      osc.stop(t + 0.3);
    });
  }

  /** One shot-clock tick during the last 10s of a player's turn — a short,
   *  neutral blip (deliberately not alarming; it repeats up to 10 times). */
  turnTick(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    this.decayTone(1200, 0.16, 0.055, ctx.currentTime, 'square', 0.9);
  }

  /** Rubbing chalk across the cue tip: a run of soft, airy scrapes (~0.7 s). */
  chalk(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t = ctx.currentTime;
    const strokes = 4;
    const stroke = 0.17; // one back-or-forth scrape
    const dur = strokes * stroke;

    const noise = this.makeNoise(dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2600; // airy, papery scrape
    bp.Q.value = 0.6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    // swell-and-ease each stroke so it sounds like a hand rubbing back and forth
    for (let i = 0; i < strokes; i++) {
      const s = t + i * stroke;
      g.gain.linearRampToValueAtTime(0.14, s + stroke * 0.45);
      g.gain.linearRampToValueAtTime(0.02, s + stroke);
    }
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.03);
    noise.connect(bp).connect(g).connect(master);
    noise.start(t);
    noise.stop(t + dur + 0.05);
  }

  /**
   * Cue tip slipping off the ball. A muffled slip-scrape under the old cue-strike's
   * hollow downward "pew" (a 900→300 Hz glide) fed through a short delay/feedback
   * so it echoes out — that hollow, echoey character (wrong for a clean strike)
   * reads perfectly as a flubbed, off-tip miscue.
   */
  miscue(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t = ctx.currentTime;

    // muffled slip: low-passed noise scrape
    const dur = 0.13;
    const noise = this.makeNoise(dur);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 850;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.38, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    noise.connect(lp).connect(ng).connect(master);
    noise.start(t);
    noise.stop(t + dur);

    // Echo bus: a short delay with moderate feedback gives the hollow tone a few
    // decaying repeats — the "echoing" tail. Dry signal still goes straight to the
    // master; the delay output is mixed in alongside it.
    const delay = ctx.createDelay(0.5);
    delay.delayTime.value = 0.085;
    const fb = ctx.createGain();
    fb.gain.value = 0.4; // ~3–4 audible repeats before it dies
    const echoOut = ctx.createGain();
    echoOut.gain.value = 0.55;
    delay.connect(fb).connect(delay); // feedback loop
    delay.connect(echoOut).connect(master);

    // The hollow downward "pew" — the old strike tone, now intentionally hollow.
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(900, t);
    osc.frequency.exponentialRampToValueAtTime(300, t + 0.05);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.32, t);
    og.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    osc.connect(og);
    og.connect(master); // dry
    og.connect(delay); // into the echo
    osc.start(t);
    osc.stop(t + 0.17);

    // a faint low thunk for the slip's weight
    this.decayTone(150, 0.22, 0.08, t, 'triangle', 0.6);
  }

  /**
   * Play an emote sound. Built-in kinds (clap, nice, cheer, whistle, boo, drumroll)
   * are synthesized here; any other key (e.g. a recorded voice line) only plays if a
   * sample was registered via setEmoteSamples — otherwise it's silently a no-op. See
   * emotes/catalog.ts.
   */
  emote(kind: string): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    if (this.playSample(kind)) return; // use the real sample when one is loaded
    const t = ctx.currentTime;
    switch (kind) {
      case 'clap': {
        // a short burst of hand claps: a handful of bandpassed noise pops
        for (let i = 0; i < 6; i++) {
          const at = t + i * 0.085 + Math.random() * 0.01;
          const dur = 0.03;
          const n = this.makeNoise(dur);
          const bp = ctx.createBiquadFilter();
          bp.type = 'bandpass';
          bp.frequency.value = 1400 + Math.random() * 500;
          bp.Q.value = 0.8;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.5, at);
          g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
          n.connect(bp).connect(g).connect(master);
          n.start(at);
          n.stop(at + dur);
        }
        break;
      }
      case 'nice': {
        // an upbeat little arpeggio (major triad up to the octave)
        const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
        notes.forEach((f, i) => this.decayTone(f, 0.4, 0.22, t + i * 0.09, 'triangle', 1));
        break;
      }
      case 'cheer': {
        // a crowd-ish swell: layered bandpassed noise that rises then fades
        const dur = 0.9;
        for (let i = 0; i < 3; i++) {
          const n = this.makeNoise(dur);
          const bp = ctx.createBiquadFilter();
          bp.type = 'bandpass';
          bp.frequency.value = 700 + i * 600;
          bp.Q.value = 0.5;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.0001, t);
          g.gain.linearRampToValueAtTime(0.22, t + 0.25);
          g.gain.linearRampToValueAtTime(0.16, t + 0.55);
          g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
          n.connect(bp).connect(g).connect(master);
          n.start(t);
          n.stop(t + dur);
        }
        break;
      }
      case 'whistle': {
        // a two-tone sports whistle with a fast warble
        const dur = 0.5;
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(2100, t);
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 18;
        const lfoG = ctx.createGain();
        lfoG.gain.value = 90; // warble depth
        lfo.connect(lfoG).connect(osc.frequency);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.linearRampToValueAtTime(0.3, t + 0.03);
        g.gain.setValueAtTime(0.3, t + dur - 0.06);
        g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.connect(g).connect(master);
        osc.start(t);
        osc.stop(t + dur);
        lfo.start(t);
        lfo.stop(t + dur);
        break;
      }
      case 'boo': {
        // a low descending sawtooth jeer
        const dur = 0.7;
        const osc = ctx.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(180, t);
        osc.frequency.exponentialRampToValueAtTime(90, t + dur);
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = 700;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.linearRampToValueAtTime(0.32, t + 0.06);
        g.gain.setValueAtTime(0.32, t + dur - 0.12);
        g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.connect(lp).connect(g).connect(master);
        osc.start(t);
        osc.stop(t + dur);
        break;
      }
      case 'drumroll': {
        // accelerating low tom hits, capped by a bright cymbal-ish crash
        let at = t;
        let gap = 0.07;
        for (let i = 0; i < 16; i++) {
          this.decayTone(120, 0.3, 0.05, at, 'sine', 0.7);
          at += gap;
          gap = Math.max(0.028, gap * 0.92); // speed up
        }
        const crash = this.makeNoise(0.5);
        const hp = ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 5000;
        const cg = ctx.createGain();
        cg.gain.setValueAtTime(0.3, at);
        cg.gain.exponentialRampToValueAtTime(0.0001, at + 0.5);
        crash.connect(hp).connect(cg).connect(master);
        crash.start(at);
        crash.stop(at + 0.5);
        break;
      }
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.ctx) {
      // Smoothly target the new level to avoid clicks when toggling.
      this.master.gain.setTargetAtTime(muted ? 0 : this.masterLevel, this.ctx.currentTime, 0.01);
    }
  }

  /** Create a one-shot white-noise buffer source lasting `seconds`. */
  private makeNoise(seconds: number): AudioBufferSourceNode {
    const ctx = this.ctx!;
    const frames = Math.max(1, Math.floor(ctx.sampleRate * seconds));
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let n = 0; n < frames; n++) data[n] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    return src;
  }
}
